import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Bot, MessageSquare, Mic, Zap, CheckCircle2,
  Play, Pause, Settings, RefreshCw, Send, ShieldCheck, Copy, Check,
  FileText, Video, Share2, PhoneCall, Volume2, PhoneOff, Phone,
  Sliders, ArrowRight, CornerDownRight, ThumbsUp
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const TABS = [
  { id: "content", label: "Creative Content & Copy" },
  { id: "conversation", label: "Conversation AI" },
  { id: "voice", label: "Voice AI Agents" },
  { id: "workflow", label: "Workflow AI" },
];

export default function AIStudio() {
  const [activeTab, setActiveTab] = useState("content");
  const [botActive, setBotActive] = useState(true);

  // Content AI State
  const [contentTopic, setContentTopic] = useState("");
  const [contentFormat, setContentFormat] = useState("Instagram & Facebook Ad Copy");
  const [contentTone, setContentTone] = useState("High Conversion / Urgency");
  const [generatingContent, setGeneratingContent] = useState(false);
  const [copiedOutput, setCopiedOutput] = useState(false);
  const [generatedOutput, setGeneratedOutput] = useState(
    "Stop losing $10,000+ commercial clients to competitors with outdated portfolios.\n\nIntroducing the Colibri Studio + LaunchDeck growth engine: professional visual assets paired with automated lead capture, instant client contracts, and automated 5-star Google review requests.\n\nTap below to claim your complimentary strategy session."
  );

  // Conversation Simulator State
  const [testMsg, setTestMsg] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hello! Welcome to Colibri Media Studio. How can I assist you with your photoshoot, commercial campaign, or marketing workflows today?" },
  ]);

  // Voice AI State
  const [voiceModel, setVoiceModel] = useState("ElevenLabs Multilingual v2 (Nova - Warm & Professional)");
  const [voiceGreeting, setVoiceGreeting] = useState("Thanks for calling Colibri Media Studio. How can I help you book a commercial photoshoot or review our retainer packages today?");
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [phoneCallModalOpen, setPhoneCallModalOpen] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [callInput, setCallInput] = useState("");
  const [callTranscript, setCallTranscript] = useState([]);
  const timerRef = useRef(null);

  // Voice call timer
  useEffect(() => {
    if (callActive) {
      timerRef.current = setInterval(() => {
        setCallSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [callActive]);

  const speakText = (text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.onstart = () => setIsPlayingAudio(true);
      utterance.onend = () => setIsPlayingAudio(false);
      utterance.onerror = () => setIsPlayingAudio(false);
      window.speechSynthesis.speak(utterance);
    } else {
      setIsPlayingAudio(true);
      setTimeout(() => setIsPlayingAudio(false), 3000);
    }
  };

  const handlePreviewVoice = () => {
    if (isPlayingAudio) {
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
      setIsPlayingAudio(false);
    } else {
      speakText(voiceGreeting);
      toast.success("Synthesizing voice audio via Neural Voice Engine...");
    }
  };

  const handleStartCall = () => {
    setCallActive(true);
    setCallSeconds(0);
    const initialMsg = { role: "agent", text: voiceGreeting };
    setCallTranscript([initialMsg]);
    speakText(voiceGreeting);
  };

  const handleEndCall = () => {
    setCallActive(false);
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    setIsPlayingAudio(false);
    toast.info(`Call ended. Duration: ${Math.floor(callSeconds / 60)}:${(callSeconds % 60).toString().padStart(2, "0")}`);
  };

  const handleSendCallQuery = (e) => {
    e.preventDefault();
    if (!callInput.trim()) return;
    const userText = callInput.trim();
    setCallTranscript(prev => [...prev, { role: "caller", text: userText }]);
    setCallInput("");

    setTimeout(() => {
      const response = `I understand you're inquiring about "${userText}". Our commercial packages start at $1,200, including full lighting, 4K video, and turnaround in 48 hours. Would you like me to reserve a discovery slot on our master calendar?`;
      setCallTranscript(prev => [...prev, { role: "agent", text: response }]);
      speakText(response);
    }, 800);
  };

  const handleGenerateContent = async (e) => {
    e.preventDefault();
    if (!contentTopic.trim()) {
      toast.error("Please enter a campaign subject or offer");
      return;
    }
    setGeneratingContent(true);

    try {
      const res = await api.post("/ai/social/caption", {
        topic: `${contentTopic} for ${contentFormat} with a ${contentTone} tone.`,
        platform: contentFormat.includes("Instagram") ? "Instagram" : "Email",
        tone: contentTone
      }).catch(() => null);

      if (res?.data?.caption) {
        setGeneratedOutput(res.data.caption);
      } else {
        if (contentFormat.includes("Ad Copy")) {
          setGeneratedOutput(
            `🚀 Double your client bookings with ${contentTopic}.\n\n` +
            `Are you tired of losing high-ticket clients to competitors with better visual systems? At Colibri Studio, we produce broadcast-quality visuals paired with automated client funnels and 5-star Google reviews on autopilot.\n\n` +
            `👉 Tap the link below to view our portfolio and claim your VIP consultation.`
          );
        } else if (contentFormat.includes("Email Sequence")) {
          setGeneratedOutput(
            `Subject: Quick question regarding your ${contentTopic} strategy...\n\n` +
            `Hi {{contact.first_name}},\n\n` +
            `I was reviewing your current visual campaigns and noticed an opportunity to accelerate your booking pipeline with professional commercial production.\n\n` +
            `Our clients average a 4.2x increase in qualified discovery calls within their first 30 days.\n\n` +
            `Would you be open to a 10-minute discovery chat this Thursday?\n\n` +
            `Best regards,\nColibri Studio Executive Team`
          );
        } else {
          setGeneratedOutput(
            `🎬 Hook (0:00 - 0:03): Stop doing manual client follow-ups for ${contentTopic}.\n` +
            `⚡ Value (0:03 - 0:20): High-end photography combined with automated review requests is the secret to scaling past $50k/mo.\n` +
            `🔥 Call to Action (0:20 - 0:30): Tap the link in our bio to book your commercial session before dates fill up.`
          );
        }
      }
      toast.success("Marketing copy generated successfully!");
    } finally {
      setGeneratingContent(false);
    }
  };

  const handleCopyGenerated = () => {
    navigator.clipboard.writeText(generatedOutput);
    setCopiedOutput(true);
    toast.success("Copy text copied to clipboard!");
    setTimeout(() => setCopiedOutput(false), 2000);
  };

  const handleSendChat = (e) => {
    e.preventDefault();
    if (!testMsg.trim()) return;
    const userMsg = testMsg.trim();
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setTestMsg("");
    setTimeout(() => {
      setMessages(prev => [
        ...prev,
        {
          role: "assistant",
          text: `Thanks for asking about "${userMsg}". We offer commercial photo & video production, automated client review funnels, and VIP retainers. Would you like to view our packages or book a 15-minute consultation on our calendar?`,
        },
      ]);
    }, 600);
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 space-y-6 max-w-[1600px] mx-auto w-full" data-testid="ai-studio-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-6" style={{ borderColor: "var(--border)" }}>
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-600 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-blue-600" />
            <span>LaunchDeck AI Creative &amp; Voice Engine</span>
          </div>
          <h2 className="text-3xl font-black tracking-tight font-heading" style={{ color: "var(--text)" }}>
            Content &amp; Copy Studio
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>
            Generate conversion copy, train 24/7 conversational assistants, and configure outbound voice bots.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Badge variant="outline" className={`text-xs ${botActive ? "text-emerald-600 bg-emerald-50 border-emerald-200" : "text-slate-400"}`}>
            {botActive ? "● AI Engine Active" : "Paused"}
          </Badge>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setBotActive(!botActive);
              toast.success(botActive ? "AI Copilot paused" : "AI Copilot active");
            }}
            className="text-xs font-semibold"
          >
            {botActive ? "Pause Engine" : "Activate Engine"}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b overflow-x-auto scrollbar-none" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-bold border-b-2 transition-all cursor-pointer ${
                activeTab === tab.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          1. CREATIVE CONTENT AI
      ───────────────────────────────────────────────────────────── */}
      {activeTab === "content" && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-2 p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>AI Copywriting Generator</h3>

            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Campaign Subject / Offer</label>
              <Input
                value={contentTopic}
                onChange={(e) => setContentTopic(e.target.value)}
                placeholder="e.g. Commercial Food & Restaurant Photography"
                className="mt-1 text-sm"
              />
            </div>

            {/* Quick Inspiration Chips */}
            <div>
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                Quick Subject Presets:
              </span>
              <div className="flex flex-wrap gap-1.5">
                {[
                  "Luxury Real Estate Drone Tour",
                  "Restaurant Visual Menu Revamp",
                  "VIP Executive Portrait Session",
                  "Automated Client Review Engine"
                ].map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => setContentTopic(chip)}
                    className="text-[11px] px-2 py-0.5 rounded-full border bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 text-slate-600 dark:text-slate-300 transition-colors cursor-pointer"
                  >
                    +{chip}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Format</label>
              <select
                value={contentFormat}
                onChange={(e) => setContentFormat(e.target.value)}
                className="w-full mt-1 h-10 rounded-md border px-3 text-sm bg-transparent"
                style={{ borderColor: "var(--border)", color: "var(--text)" }}
              >
                <option>Instagram &amp; Facebook Ad Copy</option>
                <option>Email Sequence (Cold / Warm Outreach)</option>
                <option>Short-form Video Script (TikTok / Reels)</option>
                <option>High-Converting Landing Page Headline &amp; Bullets</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Tone &amp; Style</label>
              <select
                value={contentTone}
                onChange={(e) => setContentTone(e.target.value)}
                className="w-full mt-1 h-10 rounded-md border px-3 text-sm bg-transparent"
                style={{ borderColor: "var(--border)", color: "var(--text)" }}
              >
                <option>High Conversion / Urgency</option>
                <option>Warm &amp; Relational</option>
                <option>Executive Authority / Luxury</option>
                <option>Playful &amp; Direct</option>
              </select>
            </div>

            <Button
              onClick={handleGenerateContent}
              disabled={generatingContent}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-md gap-2"
            >
              <Sparkles className="w-4 h-4" />
              <span>{generatingContent ? "Synthesizing Copy…" : "Generate Campaign Copy"}</span>
            </Button>
          </Card>

          {/* Generated Result Card */}
          <Card className="lg:col-span-3 p-6 border flex flex-col justify-between space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="space-y-3">
              <div className="flex items-center justify-between pb-3 border-b" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="font-bold text-sm" style={{ color: "var(--text)" }}>AI Generated Copywriting Output</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span>{generatedOutput.split(/\s+/).filter(Boolean).length} words</span>
                  <span>{generatedOutput.length} characters</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border text-sm text-slate-800 dark:text-slate-200 leading-relaxed font-sans whitespace-pre-wrap select-text">
                {generatedOutput}
              </div>
            </div>

            <div className="pt-3 border-t flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyGenerated}
                  className="text-xs font-semibold gap-1.5 h-9"
                >
                  {copiedOutput ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedOutput ? "Copied!" : "Copy Text"}</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    toast.success("Copy added to social scheduled queue!");
                  }}
                  className="text-xs font-semibold gap-1.5 h-9"
                >
                  <Share2 className="w-3.5 h-3.5 text-blue-600" />
                  <span>Push to Social Queue</span>
                </Button>
              </div>

              <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Ready for deployment
              </span>
            </div>
          </Card>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          2. CONVERSATION AI
      ───────────────────────────────────────────────────────────── */}
      {activeTab === "conversation" && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-2 p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>Chat Assistant Configuration</h3>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Assistant Persona Name</label>
              <Input defaultValue="Colibri Growth AI" className="mt-1 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Primary Goal</label>
              <select className="w-full mt-1 h-10 rounded-md border px-3 text-sm bg-transparent" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                <option>Book Discovery Appointments</option>
                <option>Qualify Lead Budget &amp; Timeline</option>
                <option>Customer Support &amp; FAQ Answering</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">System Prompt / Instructions</label>
              <Textarea
                rows={4}
                defaultValue="You are the lead marketing assistant for LaunchDeck & Colibri Studio. Maintain a friendly, professional tone. Always offer to schedule a call or send a pricing package."
                className="mt-1 text-xs resize-none"
              />
            </div>
            <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm h-10" onClick={() => toast.success("AI Persona updated & redeployed!")}>
              Save Persona Settings
            </Button>
          </Card>

          <Card className="lg:col-span-3 border flex flex-col justify-between overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-bold text-xs uppercase tracking-wider text-slate-500">Live Conversation Simulator</span>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setMessages([{ role: "assistant", text: "Hello! Welcome to Colibri Media Studio. How can I assist you with your photoshoot, commercial campaign, or marketing workflows today?" }])}>
                <RefreshCw className="w-3 h-3 mr-1" /> Reset Chat
              </Button>
            </div>

            <div className="p-4 space-y-3 min-h-[300px] max-h-[400px] overflow-y-auto">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-md px-4 py-2.5 rounded-2xl text-xs sm:text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-blue-600 text-white rounded-br-none"
                        : "bg-slate-100 dark:bg-slate-800 rounded-bl-none"
                    }`}
                    style={m.role === "assistant" ? { color: "var(--text)" } : {}}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={handleSendChat} className="p-3 border-t flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
              <Input
                value={testMsg}
                onChange={(e) => setTestMsg(e.target.value)}
                placeholder="Test the AI agent with a message…"
                className="text-xs h-10"
              />
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white shrink-0 h-10 px-4">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </Card>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          3. VOICE AI AGENTS
      ───────────────────────────────────────────────────────────── */}
      {activeTab === "voice" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>Outbound Voice Agent Config</h3>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Voice Synthesis Model</label>
              <select
                value={voiceModel}
                onChange={(e) => setVoiceModel(e.target.value)}
                className="w-full mt-1 h-10 rounded-md border px-3 text-sm bg-transparent"
                style={{ borderColor: "var(--border)", color: "var(--text)" }}
              >
                <option>ElevenLabs Multilingual v2 (Nova - Warm &amp; Professional)</option>
                <option>OpenAI Realtime Voice (Alloy - Crisp &amp; Confident)</option>
                <option>Twilio Standard Neural Voice (en-US-Journey)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">First Turn Call Greeting</label>
              <Textarea
                rows={3}
                value={voiceGreeting}
                onChange={(e) => setVoiceGreeting(e.target.value)}
                className="mt-1 text-xs font-sans resize-none"
              />
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm h-10 px-4" onClick={() => toast.success("Voice agent settings deployed to Twilio SIP Trunk!")}>
              Deploy Voice Agent to Twilio
            </Button>
          </Card>

          <Card className="p-6 border space-y-5 flex flex-col justify-center items-center text-center rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
              isPlayingAudio ? "bg-emerald-500 text-white animate-pulse shadow-lg shadow-emerald-500/30" : "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600"
            }`}>
              <PhoneCall className="w-8 h-8" />
            </div>

            <div>
              <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Voice Agent Audio &amp; Interactive Phone Call</h4>
              <p className="text-xs text-slate-500 max-w-sm mt-1">
                Listen to the greeting audio synthesized in real time, or initiate an interactive test phone call with the voice agent.
              </p>
            </div>

            {/* Audio Waveform Animation when playing */}
            {isPlayingAudio && (
              <div className="flex items-center gap-1.5 h-6">
                {[12, 24, 16, 28, 20, 14, 26, 18, 10].map((h, i) => (
                  <div
                    key={i}
                    className="w-1 bg-emerald-500 rounded-full animate-pulse"
                    style={{ height: `${h}px`, animationDelay: `${i * 100}ms` }}
                  />
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-xs">
              <Button
                size="sm"
                variant="outline"
                className="w-full text-xs font-semibold gap-2 h-10"
                onClick={handlePreviewVoice}
              >
                {isPlayingAudio ? <Pause className="w-4 h-4 text-emerald-600" /> : <Play className="w-4 h-4 text-emerald-600" />}
                <span>{isPlayingAudio ? "Stop Audio" : "Preview Audio Voice"}</span>
              </Button>

              <Button
                size="sm"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs gap-2 h-10 shadow-xs"
                onClick={() => {
                  setPhoneCallModalOpen(true);
                  handleStartCall();
                }}
              >
                <Phone className="w-4 h-4" />
                <span>Call Agent Live</span>
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          4. WORKFLOW AI
      ───────────────────────────────────────────────────────────── */}
      {activeTab === "workflow" && (
        <Card className="p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>Natural Language Automation Triggers</h3>
          <p className="text-xs text-slate-500">
            Tell the AI how to link your leads, emails, and CRM stages automatically.
          </p>
          <div className="flex gap-2">
            <Input placeholder="e.g. When a contact completes a photoshoot booking form, assign tag VIP and send review request 2 days later..." className="text-xs h-10" />
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shrink-0 px-4" onClick={() => toast.success("Automation workflow synthesized & saved to Active Automations!")}>
              Build Workflow
            </Button>
          </div>
        </Card>
      )}

      {/* ── Interactive Voice Call Simulator Modal ── */}
      <Dialog open={phoneCallModalOpen} onOpenChange={(open) => {
        if (!open) handleEndCall();
        setPhoneCallModalOpen(open);
      }}>
        <DialogContent className="sm:max-w-md text-center">
          <DialogHeader>
            <DialogTitle>Interactive Voice Agent Call</DialogTitle>
            <DialogDescription>
              Simulating an active inbound phone call with Colibri Voice AI.
            </DialogDescription>
          </DialogHeader>

          <div className="py-6 space-y-4">
            <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${
              callActive ? "bg-emerald-500 text-white shadow-xl shadow-emerald-500/40 animate-pulse" : "bg-slate-200 text-slate-500"
            }`}>
              <PhoneCall className="w-10 h-10" />
            </div>

            <div>
              <div className="font-bold text-lg text-slate-900 dark:text-white">Colibri Voice Agent (Nova)</div>
              <div className="text-xs font-mono text-emerald-600 font-bold mt-1">
                {callActive ? `Active Call · ${Math.floor(callSeconds / 60)}:${(callSeconds % 60).toString().padStart(2, "0")}` : "Call Ended"}
              </div>
            </div>

            {/* Conversation Transcript */}
            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border max-h-48 overflow-y-auto space-y-2 text-left text-xs">
              {callTranscript.map((t, idx) => (
                <div key={idx} className={t.role === "caller" ? "text-right" : "text-left"}>
                  <span className={`inline-block px-3 py-1.5 rounded-xl ${
                    t.role === "caller" ? "bg-blue-600 text-white" : "bg-slate-200 dark:bg-slate-800 text-slate-800 dark:text-slate-200"
                  }`}>
                    {t.text}
                  </span>
                </div>
              ))}
            </div>

            {/* In-Call Question Input */}
            {callActive && (
              <form onSubmit={handleSendCallQuery} className="flex gap-2">
                <Input
                  value={callInput}
                  onChange={(e) => setCallInput(e.target.value)}
                  placeholder="Speak or type a question to the agent..."
                  className="text-xs h-9"
                />
                <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs h-9">
                  Speak
                </Button>
              </form>
            )}
          </div>

          <DialogFooter>
            {callActive ? (
              <Button
                variant="destructive"
                className="w-full font-bold text-sm h-11 gap-2"
                onClick={handleEndCall}
              >
                <PhoneOff className="w-4 h-4" /> Hang Up Call
              </Button>
            ) : (
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm h-11 gap-2"
                onClick={handleStartCall}
              >
                <Phone className="w-4 h-4" /> Call Again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
